import React, { useState } from "react";

import {
  alpha,
  Alert,
  Card,
  CardContent,
  Chip,
  Typography,
  Button,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  Theme,
  useTheme,
} from "@mui/material";
import { addMonths, format } from "date-fns";
import { makeStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import { Package, SubscriptionStatus } from "@/gql/graphql";
import { FragmentType } from "@/utils/type";

import SubscriptionLoader from "./SubscriptionLoader";

const useStyles = makeStyles()((theme: Theme) => ({
  // Page-level card: 24px of padding all around (theme spacing unit is 4px).
  content: {
    padding: theme.spacing(6),
    "&:last-child": {
      paddingBottom: theme.spacing(6),
    },
  },
  bold: {
    fontWeight: "bold",
  },
  grey: {
    color: theme.palette.grey[600],
  },
  planCard: {
    position: "relative",
    flex: "1 1 160px",
    margin: 0,
    alignItems: "flex-start",
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    // NOTE: this theme's spacing unit is 4px, not MUI's default 8px.
    padding: theme.spacing(4),
    cursor: "pointer",
    transitionProperty: "box-shadow, border-color, background-color",
    transitionDuration: "120ms",
    "&:hover": {
      borderColor: theme.palette.grey[400],
    },
    // The radio input is visually hidden but keeps focus, so keyboard users see a ring.
    "&:focus-within": {
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.4)}`,
    },
  },
  planCardSelected: {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
  },
  planCardRadio: {
    position: "absolute",
    opacity: 0,
    pointerEvents: "none",
  },
  recommendedChip: {
    position: "absolute",
    top: theme.spacing(-2.5),
    right: theme.spacing(3),
  },
}));

// One-time Xendit payments while auto-debit approval is pending: each payment buys one period.
// `months` mirrors the backend package durations — used to preview the resulting end date.
const PLANS = [
  {
    package: Package.LitePhp,
    label: "Monthly",
    price: "₱650",
    per: "1 month of access",
    months: 1,
    recommended: false,
  },
  {
    package: Package.LitePhpQuarterly,
    label: "Quarterly",
    price: "₱1,950",
    per: "3 months of access",
    months: 3,
    recommended: true,
  },
  {
    package: Package.LitePhpAnnual,
    label: "Annual",
    price: "₱6,500",
    per: "12 months for the price of 10",
    months: 12,
    recommended: false,
  },
] as const;

const PLAN_LABELS: Partial<Record<Package, string>> = {
  [Package.Lite]: "NutriCoach Lite",
  [Package.LitePhp]: "NutriCoach Lite — Monthly",
  [Package.LitePhpQuarterly]: "NutriCoach Lite — Quarterly",
  [Package.LitePhpAnnual]: "NutriCoach Lite — Annual",
};

type Props = {
  loading: boolean;
  data: FragmentType<typeof currentSubscriptionCardFragment> | undefined;
  onCheckout: (selectedPackage: Package) => Promise<void>;
  onCancelSubscription: () => Promise<void>;
};

const currentSubscriptionCardFragment = graphql(`
  fragment CurrentSubscriptionCard on Query {
    coachViewer {
      id
      subscribedPackage
      subscriptionStatus
      subscriptionCurrentPeriodEnd
      subscriptionCancelAt
      subscriptionWillEnd
      autoRenewing
    }
  }
`);

const CurrentSubscriptionCard: React.FC<Props> = ({
  onCheckout,
  onCancelSubscription,
  loading,
  data,
}) => {
  const { classes, cx } = useStyles();
  const theme = useTheme();
  const coach = data?.coachViewer;
  const isTrialing = coach?.subscriptionStatus === SubscriptionStatus.Trialing;
  const isSuspended =
    coach?.subscriptionStatus === SubscriptionStatus.Canceled ||
    coach?.subscriptionStatus === SubscriptionStatus.Unpaid;
  // The recommended plan is also the default selection — recommending one thing and
  // pre-selecting another reads as a dark pattern.
  const [selectedPackage, setSelectedPackage] = useState<Package>(
    Package.LitePhpQuarterly
  );
  const [pickerOpened, setPickerOpened] = useState(false);

  const currentPlan =
    (coach?.subscribedPackage && PLAN_LABELS[coach.subscribedPackage]) ||
    "Free trial";
  const paidUntil = coach?.subscriptionCurrentPeriodEnd
    ? format(new Date(coach.subscriptionCurrentPeriodEnd), "dd MMM, yyyy")
    : null;
  // A pending cancellation: auto-debit already stopped, access runs until this date.
  const cancelAt =
    coach?.subscriptionCancelAt &&
    new Date(coach.subscriptionCancelAt) > new Date()
      ? format(new Date(coach.subscriptionCancelAt), "dd MMM, yyyy")
      : null;
  // Cancelling only makes sense while a recurring plan would auto-debit again; one-time
  // payers just stop renewing.
  const canCancel = Boolean(coach?.autoRenewing) && !cancelAt && !isSuspended;

  // Progressive disclosure: lead with the current plan, and open the picker only when paying is
  // the coach's actual task — choosing (trial), renewing (period ending / lapsed / cancelled), or
  // on explicit request.
  const mustPickPlan =
    isTrialing ||
    isSuspended ||
    coach?.subscriptionStatus === SubscriptionStatus.PastDue ||
    Boolean(coach?.subscriptionWillEnd) ||
    Boolean(cancelAt);
  const showPicker = mustPickPlan || pickerOpened;

  // Payments stack: a renewal extends from the current period end, a fresh/lapsed one from today.
  const selectedPlan = PLANS.find((plan) => plan.package === selectedPackage);
  const hasTimeLeft = Boolean(
    coach?.subscriptionCurrentPeriodEnd &&
      new Date(coach.subscriptionCurrentPeriodEnd) > new Date() &&
      !isTrialing
  );
  const newEndDate = selectedPlan
    ? format(
        addMonths(
          hasTimeLeft
            ? new Date(coach!.subscriptionCurrentPeriodEnd!)
            : new Date(),
          selectedPlan.months
        ),
        "dd MMM, yyyy"
      )
    : null;

  return (
    <Card>
      <CardContent className={classes.content}>
        {loading ? (
          <SubscriptionLoader />
        ) : (
          <>
            <Box
              flex={1}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              marginBottom={theme.spacing(0.5)}
            >
              <Typography variant="body1" className={classes.bold}>
                My current plan
              </Typography>
              {paidUntil && !isTrialing && (
                <Typography variant="subtitle2" className={classes.grey}>
                  {coach?.autoRenewing && !cancelAt
                    ? "Renews on"
                    : "Access until"}
                  : {paidUntil}
                </Typography>
              )}
            </Box>
            <Typography variant="h5" className={classes.bold} gutterBottom>
              {isTrialing ? "Free trial" : currentPlan}
            </Typography>
            {cancelAt && (
              <Alert severity="warning" sx={{ marginY: 2 }}>
                Your subscription is cancelled — auto-renewal is off and your
                access ends on {cancelAt}.
              </Alert>
            )}
            {coach?.subscriptionWillEnd && !isSuspended && paidUntil && (
              <Alert severity="warning" sx={{ marginY: 2 }}>
                Your access ends on {paidUntil} — renew below to keep your
                account active.
              </Alert>
            )}

            {!showPicker && (
              <Box marginTop={6}>
                <Button
                  variant="outlined"
                  onClick={() => setPickerOpened(true)}
                >
                  Renew or change plan
                </Button>
              </Box>
            )}
            {showPicker && (
              <Box marginTop={6}>
                <Typography
                  variant="body1"
                  className={classes.bold}
                  gutterBottom
                >
                  {isTrialing ? "Choose a plan" : "Renew or change your plan"}
                </Typography>
                <RadioGroup
                  row
                  sx={{ gap: 3, paddingTop: 2.5, alignItems: "stretch" }}
                  value={selectedPackage}
                  onChange={(event) =>
                    setSelectedPackage(event.target.value as Package)
                  }
                >
                  {PLANS.map((plan) => (
                    <FormControlLabel
                      key={plan.package}
                      value={plan.package}
                      className={cx(
                        classes.planCard,
                        plan.package === selectedPackage &&
                          classes.planCardSelected
                      )}
                      control={<Radio className={classes.planCardRadio} />}
                      label={
                        <Box>
                          {plan.recommended && (
                            <Chip
                              className={classes.recommendedChip}
                              label="Recommended"
                              color="primary"
                              size="small"
                            />
                          )}
                          <Typography variant="body1" className={classes.bold}>
                            {plan.label}
                          </Typography>
                          <Typography variant="h6" className={classes.bold}>
                            {plan.price}
                          </Typography>
                          <Typography
                            variant="subtitle2"
                            className={classes.grey}
                          >
                            {plan.per}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </RadioGroup>
                {!coach?.autoRenewing && (
                  <Alert severity="info" sx={{ marginY: 3 }}>
                    Auto-renewal is coming soon. For now payments are one-time —
                    we will remind you before your access ends so you can renew.
                  </Alert>
                )}
                {newEndDate && (
                  <Typography
                    variant="subtitle2"
                    className={classes.grey}
                    gutterBottom
                  >
                    {hasTimeLeft
                      ? `Renewing now extends your current plan — access until ${newEndDate}.`
                      : `Pay today and your access runs until ${newEndDate}.`}
                  </Typography>
                )}
                <Button
                  variant="contained"
                  sx={{ marginTop: 1 }}
                  onClick={() => onCheckout(selectedPackage)}
                >
                  Pay {selectedPlan?.price} to Renew
                </Button>
              </Box>
            )}
            {canCancel && (
              <Button
                variant="outlined"
                color="error"
                sx={{ marginTop: 4 }}
                onClick={onCancelSubscription}
              >
                Cancel subscription
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CurrentSubscriptionCard;
